import { type HeaderItem, type LinkItem, type MenuItem } from './types';

export interface Tellingen {
  aantalSchades: number;
  aantalAanvragen: number;
  aantalAanvragenmutaties: number;
  aantalOoditchanges: number;
  aantalPremievastellingen: number;
  aantalMaatwerk: number;
  spfMeldingen: number;
}
interface EnvironmentSettings {
  isMaatschappij: boolean;
  isInterneGebruiker: boolean;
  isFinancieel: boolean;
  isInstellingsRechten: boolean;
  isActiveAdviseur: boolean;
  heeftAdviseurbeheerRechten: boolean;
  heeftBeheerboxRechten: boolean;
  heeftRoyerenPermissie: boolean;
  heeftMutatieStartenPermissie: boolean;
  heeftVoorlopigeDekkingPermissie: boolean;
  tellingen?: Tellingen;
  nieuweOffrerenPagina: boolean;
  nieuweAanvragenPagina: boolean;
  nieuweSchadeEnMaatwerkPagina: boolean;
}

function createHeaderItem(title: string): HeaderItem {
  return { itemtype: 'header', title };
}

const defaultTellingen: Tellingen = {
  aantalSchades: 0,
  aantalAanvragen: 0,
  aantalAanvragenmutaties: 0,
  aantalOoditchanges: 0,
  aantalPremievastellingen: 0,
  aantalMaatwerk: 0,
  spfMeldingen: 0,
};

export function createMenuItems(settings: EnvironmentSettings): MenuItem[] {
  const {
    isMaatschappij,
    isInterneGebruiker,
    isFinancieel,
    isInstellingsRechten,
    isActiveAdviseur,
    heeftAdviseurbeheerRechten,
    heeftRoyerenPermissie,
    heeftMutatieStartenPermissie,
    tellingen = defaultTellingen,
    heeftBeheerboxRechten,
    heeftVoorlopigeDekkingPermissie,
    nieuweOffrerenPagina,
    nieuweAanvragenPagina,
    nieuweSchadeEnMaatwerkPagina,
  } = settings;
  const items: MenuItem[] = [];

  // Voeg standaard items toe
  items.push({
    itemtype: 'link',
    title: 'Dashboard',
    url: '/app/aanvraag/recente-activiteiten/',
    iconclass: 'fa-solid fa-house-window',
    target: '_top',
  });

  if (isActiveAdviseur || isInterneGebruiker || isMaatschappij) {
    items.push({
      itemtype: 'link',
      title: 'Risicoscan',
      url: '/net/riskinventarisation/',
      iconclass: 'fas fa-wrench',
    });
  }

  if (heeftVoorlopigeDekkingPermissie) {
    items.push({
      itemtype: 'link',
      title: 'Voorlopige dekkingen',
      url: '/app/offerte/voorlopige-dekkingen/',
      iconclass: 'fas fa-car',
      target: '_top',
    });
  }

  if (isActiveAdviseur || isInterneGebruiker || isMaatschappij) {
    items.push({
      itemtype: 'link',
      title: 'Offreren / Aanvragen',
      url: nieuweOffrerenPagina
        ? '/app/aanvraag/recente-activiteiten/offreren-aanvragen'
        : '/content/boaz/',
      iconclass: 'fas fa-calculator',
    });
  }

  if (heeftRoyerenPermissie || heeftMutatieStartenPermissie) {
    const title =
      heeftMutatieStartenPermissie && heeftRoyerenPermissie
        ? 'Muteren / Royeren'
        : heeftMutatieStartenPermissie && !heeftRoyerenPermissie
          ? 'Muteren'
          : 'Royeren';

    items.push({
      itemtype: 'link',
      title,
      url: '/app/crm/relatiegegevens?muteren=true',
      iconclass: 'fas fa-file-signature',
      target: '_top',
    });
  }

  items.push({
    itemtype: 'link',
    title: 'Relaties',
    url: '/app/crm/relatiegegevens',
    iconclass: 'fas fa-network-wired',
    target: '_top',
  });

  items.push({
    itemtype: 'link',
    title: 'Schade',
    url: nieuweSchadeEnMaatwerkPagina
      ? '/app/aanvraag/recente-activiteiten/schade'
      : '/content/schade',
    iconclass: 'fas fa-car-burst',
    showbadge: tellingen.aantalSchades > 0,
    badgecontent: tellingen.aantalSchades,
  });

  items.push(createHeaderItem('Aanvragen'));

  if (isActiveAdviseur || isInterneGebruiker) {
    items.push({
      itemtype: 'link',
      title: 'Aanvragen',
      url: nieuweAanvragenPagina
        ? '/app/aanvraag/recente-activiteiten/aanvragen'
        : '/content/boaz/aanvraag/?typeaanvraag=aanvraag',
      iconclass: 'far fa-calculator',
      showbadge: tellingen.aantalAanvragen > 0,
      badgecontent: tellingen.aantalAanvragen,
    });

    items.push({
      itemtype: 'link',
      title: 'Mutaties',
      url: nieuweAanvragenPagina
        ? '/app/aanvraag/recente-activiteiten/mutaties'
        : '/content/boaz/aanvraag/?typeaanvraag=mutatie',
      iconclass: 'far fa-pen-to-square',
      showbadge: tellingen.aantalAanvragenmutaties > 0,
      badgecontent: tellingen.aantalAanvragenmutaties,
    });

    if (nieuweAanvragenPagina && isInterneGebruiker) {
      items.push({
        itemtype: 'link',
        title: 'Overvoer',
        url: '/app/aanvraag/recente-activiteiten/overvoer',
        iconclass: 'far fa-file-import',
      });
    }

    items.push({
      itemtype: 'link',
      title: 'Inspectierapporten',
      url: '/app/aanvraag/inspectie/',
      iconclass: 'fas fa-clipboard-list-check',
      target: '_top',
    });

    items.push({
      itemtype: 'link',
      title: 'Premievaststelling',
      url: '/app/offerte/premievaststelling',
      iconclass: 'fas fa-percent',
      showbadge: tellingen.aantalPremievastellingen > 0,
      badgecontent: tellingen.aantalPremievastellingen,
      target: '_top',
    });
  }

  if (!isMaatschappij || isInterneGebruiker) {
    items.push({
      itemtype: 'link',
      title: 'Prolongatie',
      url: '/net/report/prolongatieoverzicht',
      iconclass: 'fas fa-arrows-rotate',
    });
  }

  if (isInterneGebruiker) {
    items.push({
      itemtype: 'link',
      title: 'Werkvoorraad',
      url: '/net/rapportageintern/werkvoorraad',
      iconclass: 'fas fa-box',
    });

    items.push({
      itemtype: 'link',
      title: 'PTS overzicht',
      url: '/app/beurs/pts/dossiers',
      iconclass: 'fas fa-bullseye-arrow',
      target: '_top',
    });
  }

  if (isActiveAdviseur || isMaatschappij || isInterneGebruiker) {
    items.push({
      itemtype: 'link',
      title: 'Maatwerk',
      url: nieuweSchadeEnMaatwerkPagina
        ? '/app/aanvraag/recente-activiteiten/maatwerk'
        : '/content/boaz/maatwerk',
      iconclass: 'fas fa-ruler',
      showbadge: tellingen.aantalMaatwerk > 0,
      badgecontent: tellingen.aantalMaatwerk,
    });
  }

  if (isActiveAdviseur || isInterneGebruiker) {
    items.push({
      itemtype: 'link',
      title: 'Alerts',
      url: '/app/crm/alerts/polisalerts',
      iconclass: 'fas fa-bell-on',
      target: '_top',
    });
  }

  if (isInterneGebruiker) {
    if (heeftBeheerboxRechten || heeftAdviseurbeheerRechten) {
      items.push(createHeaderItem('Beheer'));
    }

    if (heeftBeheerboxRechten) {
      items.push({
        itemtype: 'link',
        title: 'BeheerBox',
        url: '/app/beheerbox/',
        iconclass: 'far fa-screwdriver-wrench',
        target: '_top',
      });
    }

    if (heeftAdviseurbeheerRechten) {
      items.push({
        itemtype: 'link',
        title: 'Adviseurbeheer',
        url: '/app/crm/adviseurs',
        target: '_top',
        iconclass: 'far fa-handshake-simple',
      });
    }

    items.push({
      itemtype: 'link',
      title: 'Rapportage',
      url: '/content/rapportage/intern/',
      iconclass: 'far fa-chart-pie',
    });

    items.push({
      itemtype: 'link',
      title: 'SB Landkaart',
      url: '/net/rapportageintern/SBLandkaart/',
      target: '_blank',
      iconclass: 'far fa-map-location-dot',
    });
  }

  if (isActiveAdviseur || isMaatschappij || isInterneGebruiker) {
    items.push(createHeaderItem('Overzichten'));
  }

  items.push({
    itemtype: 'link',
    title: 'Contact',
    url: '/app/crm/contact',
    target: '_top',
    iconclass: 'far fa-phone-volume',
  });

  const instellingenItem: LinkItem = {
    itemtype: 'link',
    title: 'Instellingen',
    url: '/content/beheer/tussenpersoon/',
    iconclass: 'far fa-cog',
  };
  if (isInstellingsRechten && tellingen.spfMeldingen > 0) {
    instellingenItem.showbadge = true;
    instellingenItem.badgecontent = '1';
  }

  items.push(instellingenItem);

  items.push({
    itemtype: 'link',
    title: 'Overzichten',
    url: '/content/rapportage/',
    iconclass: 'far fa-list',
  });

  if (isFinancieel) {
    items.push({
      itemtype: 'link',
      title: 'Financieel',
      url: '/content/rapportage?fin=1',
      iconclass: 'fas fa-coins',
    });
  }

  return items;
}
